import React, { useState } from 'react';
import { Eye, User, Mail, Phone, MessageSquare, Calendar, Hash, Send } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { abvigAPI } from '../../services/api';
import { abvigAdminSupabase } from '../../services/supabaseClient';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel, truncateText } from '../../utils/helpers';
import { sendEmail } from '../../services/emailService';
import toast from 'react-hot-toast';

export default function ContactMessages() {
  const { data, refetch } = useSupabase(abvigAPI.getContactMessages);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleView = (item) => {
    setSelectedMessage(item);
    setIsModalOpen(true);
    setShowEmailForm(false);
    setEmailSubject(`Re: ${item.subject || 'Contact Form Submission'}`);
    setEmailMessage(`Dear ${item.full_name || 'Sir/Madam'},

Thank you for reaching out to us. We have received your message regarding "${item.subject || 'your inquiry'}".

We will get back to you shortly.

Best regards,
ABVIG Support Team`);
  };

  const handleSendEmail = async () => {
    if (!selectedMessage) return;
    
    setIsSendingEmail(true);
    try {
      console.log('📧 Sending email to:', selectedMessage.email);
      
      // Step 1: Send email using EmailJS
      await sendEmail({
        to: selectedMessage.email,
        subject: emailSubject,
        message: emailMessage,
        fromName: process.env.REACT_APP_SENDER_NAME || 'ABVIG Support Team',
      });
      
      console.log('✅ Email sent successfully');
      
      // Step 2: Update status to 'resolved' directly using admin client
      console.log('🔄 Updating status to resolved for message:', selectedMessage.id);
      
      const { data, error } = await abvigAdminSupabase
        .from('contact_messages')
        .update({ status: 'resolved' })
        .eq('id', selectedMessage.id)
        .select();
      
      if (error) {
        console.error('❌ Supabase error:', error);
        toast.error(`Status update failed: ${error.message}`);
        throw error;
      }
      
      console.log('✅ Status update result:', data);
      
      if (data && data.length > 0) {
        toast.success(`✅ Email sent and status updated to Resolved`);
      } else {
        toast.warning('Email sent but status may not have updated');
      }
      
      // Step 3: Refresh data
      await refetch();
      
      // Step 4: Close modal and reset
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
      setIsModalOpen(false);
      setSelectedMessage(null);
      
    } catch (error) {
      console.error('❌ Error:', error);
      toast.error(`Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const renderMessageDetails = (message) => {
    if (!message) return null;

    if (showEmailForm) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Compose Reply Email</h4>
            <button
              onClick={() => setShowEmailForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={isSendingEmail}
            >
              Back to Details
            </button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">From:</p>
            <p className="text-sm font-medium text-gray-900">{process.env.REACT_APP_SENDER_NAME || 'ABVIG Support Team'} &lt;{process.env.REACT_APP_SENDER_EMAIL || 'support@abvig.com'}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-gray-900">{message.email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email subject"
              disabled={isSendingEmail}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows="8"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Type your reply here..."
              disabled={isSendingEmail}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailSubject || !emailMessage}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Send Reply'}
            </button>
            <button
              onClick={() => setShowEmailForm(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSendingEmail}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <User className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Full Name</p>
              <p className="text-sm font-medium text-gray-900">{message.full_name || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm font-medium text-gray-900">{message.email || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm font-medium text-gray-900">{message.phone || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Subject</p>
              <p className="text-sm font-medium text-gray-900">{message.subject || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Received</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(message.created_at)}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-400">#</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <span className={`badge ${getStatusColor(message.status)} mt-1`}>
                {getStatusLabel(message.status)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Message
          </h4>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.message || 'No message content'}</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          {message.status === 'pending' && (
            <button
              onClick={() => setShowEmailForm(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Send className="w-4 h-4" />
              Respond with Email
            </button>
          )}

          <div className="pt-2">
            <p className="text-sm text-gray-500 text-center">
              Status: <span className={`font-semibold ${message.status === 'resolved' ? 'text-green-600' : message.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'}`}>
                {getStatusLabel(message.status)}
              </span>
            </p>
            {message.status === 'resolved' && (
              <p className="text-xs text-gray-400 text-center mt-1">
                This message has been responded to
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'full_name', label: 'Full Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'subject', label: 'Subject' },
    { 
      key: 'message', 
      label: 'Message',
      render: (value) => truncateText(value, 30)
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Received',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'resolved', 'rejected']
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
        <p className="text-gray-600">View and respond to contact form submissions</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} messages`}
        actions={actions}
        searchPlaceholder="Search messages..."
        onRowClick={(item) => handleView(item)}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMessage(null);
          setShowEmailForm(false);
        }}
        title={showEmailForm ? "Compose Reply" : "Message Details"}
        size="max-w-2xl"
      >
        {renderMessageDetails(selectedMessage)}
      </Modal>
    </div>
  );
}